import {nextTick, ref, watch} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {createError, useCookie, useNuxtApp} from '#app';
import {useLocale, useLogger, useTranslation} from '#imports';
import type {LegalNameAutocompleteCompanySize, LegalNameAutocompleteUpdatePayload} from '@malt/nuxt-micro-frontend-types';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ClientDepartmentRequest, CompanyIndustryRequest} from '@api';
import {
    ClientSignupApi,
    ClientSignupEmailAddressValidityApi,
    type CompanyLegalDetails,
    CompanySizeRequest,
    type EmailAddressValidity,
    type EmailAddressValidityResponse,
    FetchError,
    instanceOfClientDepartmentRequest,
    instanceOfCompanyIndustryRequest,
    instanceOfCompanySizeRequest,
    type QuestionAnswersRequest,
    type SignupStateResponse,
} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';
import {isStepRedirectUser} from '~/lib/steps';
import {useContextStore} from './context.store';

interface ResponseWrapper<T> {
    data: T | null;
    error: FetchError | null;
}

interface UserDetailsRequest {
    firstName: string;
    lastName: string;
    countryCode?: string;
    phoneNumber: string;
    optOutFromNewsletter: boolean;
}

function mapLegalCompanySizeToSignup(legalSize: LegalNameAutocompleteCompanySize): CompanySizeRequest {
    switch (legalSize) {
        case 'INDIVIDUAL':
            return CompanySizeRequest.Individual;
        case 'BETWEEN_2_AND_10':
            return CompanySizeRequest.Between2And10;
        case 'BETWEEN_11_AND_49':
            return CompanySizeRequest.Between11And49;
        case 'BETWEEN_50_AND_249':
            return CompanySizeRequest.Between50And249;
        case 'BETWEEN_250_AND_999':
            return CompanySizeRequest.Between250And999;
        case 'BETWEEN_1000_AND_4999':
            return CompanySizeRequest.Between1000And4999;
        case 'MORE_THAN_5000':
            return CompanySizeRequest.MoreThan5000;
    }
}

export const useSignupStore = defineStore('signup-store', () => {
    const {$pinia} = useNuxtApp();
    const logger = useLogger();
    const {t} = useTranslation();

    const contextStore = useContextStore($pinia);
    const {updateStoredValues} = contextStore;
    const {email} = storeToRefs(contextStore);

    const {isMultiLocaleHost} = useLocale();

    const signupApi = new ClientSignupApi(apiConfiguration);
    const emailAddressValidityApi = new ClientSignupEmailAddressValidityApi(apiConfiguration);

    // This cookie is used by redirect-user-to-workflow-in-progress.global.ts
    // to determine if it should verify whether a workflow is in progress.
    const pathOfWorkflowInProgressCookie = useCookie<string | null>('path-of-mandatory-workflow-likely-in-progress');

    // This state is used to determine if the user returned to the email validation step (from page reload or after dropping off)
    const redirectedOnEmailValidation = ref(false);

    const pendingRequest = ref(false);

    const emailModel = ref('');
    const emailValidity = ref<EmailAddressValidity | null>();
    const passwordModel = ref('');
    const newsletterTickedModel = ref(false);
    const validationCodeModel = ref('');
    const firstNameModel = ref('');
    const lastNameModel = ref('');
    const countryModel = ref('');
    const phoneNumberModel = ref('');
    const companyNameModel = ref('');
    const companyWebsiteUrlModel = ref('');
    const companySizeModel = ref('');
    const companySectorModel = ref('');
    const companyIndustryModel = ref('');
    const clientDepartmentModel = ref('');
    const companyLegalDetailsModel = ref<CompanyLegalDetails | null>(null);

    const redirectPath = ref('');

    watch(emailModel, () => {
        if (!emailModel.value) {
            emailValidity.value = null;
        }
    });

    watch(companySectorModel, () => {
        companyIndustryModel.value = '';
    });

    function updateSignupInProgressCookie(state: SignupStateResponse) {
        const clientSignupPath = '/signup-client';
        if (state.status === 'IN_PROGRESS') {
            pathOfWorkflowInProgressCookie.value = clientSignupPath;
        } else if (pathOfWorkflowInProgressCookie.value === clientSignupPath) {
            pathOfWorkflowInProgressCookie.value = null;
        }
    }

    function setRedirectPathIfNeeded(state: SignupStateResponse) {
        if (isStepRedirectUser(state.nextStep)) {
            redirectPath.value = state.nextStep.redirectPath as string;
        }
    }

    function updateSignupInProgressCookieAndSetRedirectPathIfNeeded(state: SignupStateResponse) {
        updateSignupInProgressCookie(state);
        setRedirectPathIfNeeded(state);
    }

    function setRedirectedOnEmailValidation() {
        redirectedOnEmailValidation.value = true;
    }

    async function getSignupState(): Promise<SignupStateResponse> {
        try {
            const data = await signupApi.getState();

            const state = data;
            updateStoredValues(state);
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(state);
            return state;
        } catch (error) {
            throw createError(error as FetchError);
        }
    }

    async function cancelSignup() {
        try {
            const signupStateResponse = await signupApi.cancelSignup();
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(signupStateResponse);
            return true;
        } catch (e) {
            if (!(e instanceof FetchError && e.statusCode === 400)) {
                logger.error(e);
            }
            return false;
        }
    }

    async function getEmailAddressValidity(): Promise<EmailAddressValidityResponse | void> {
        try {
            const payload = {email: emailModel.value};
            const response = await emailAddressValidityApi.getEmailAddressValidity(payload);
            if (response) {
                emailValidity.value = response.validity;
            }
        } catch (e) {
            logger.error(e);
        }
    }

    function resetEmailValidity(): void {
        emailValidity.value = null;
    }

    async function saveAuthenticationDetails(redirectPath?: string): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const payload = {
                signupInitializationRequestWithAuthenticationDetails: {
                    email: emailModel.value,
                    password: passwordModel.value,
                    redirectPath,
                },
            };

            const signupStateResponse = await signupApi.initSignupWithAuthenticationDetails(payload);

            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(signupStateResponse);

            return {data: signupStateResponse, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function acknowledgePublicEmailWarningPage(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const signupStateResponse = await signupApi.acknowledgePublicEmailWarningPage();

            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(signupStateResponse);

            return {data: signupStateResponse, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function userAskToChangeEmail(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const signupStateResponse = await signupApi.userAskToChangeEmail();

            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(signupStateResponse);

            return {data: signupStateResponse, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function requestNewValidationCode(): Promise<void> {
        try {
            await signupApi.requestNewValidationCode();
            pushVJoySnackbar({props: {message: t('signup.client.email.validation.page.new.code.sent', [email.value]), level: 'success'}});
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 429) {
                pushVJoySnackbar({props: {message: t('signup.client.email.validation.page.wait.for.requesting.new.code', [10]), level: 'warning'}});
                return;
            }

            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
        }
    }

    function resetEmailAndValidationCode(): void {
        emailModel.value = '';
        validationCodeModel.value = '';
    }

    async function validateEmailAddress(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const payload = {emailValidationRequest: {validationCode: validationCodeModel.value}};
            const data = await signupApi.validateEmailAddress(payload);
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(data);
            return {data, error: null};
        } catch (e) {
            if (e instanceof FetchError && e.statusCode === 400) {
                pushVJoySnackbar({props: {message: t('signup.client.email.validation.page.code.invalid'), level: 'error'}});
            } else {
                logger.error(e);
                pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            }
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function chooseCompanyToJoin(idOfCompanyToJoin: string) {
        try {
            pendingRequest.value = true;
            const payload = {companyChoiceRequest: {idOfCompanyToJoin}};
            const data = await signupApi.chooseCompanyToJoin(payload);
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(data);
            return {data, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function saveUserDetails(isNewsletterOptOutLogic: boolean): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const payload: {userDetailsRequest: UserDetailsRequest} = {
                userDetailsRequest: {
                    firstName: firstNameModel.value,
                    lastName: lastNameModel.value,
                    phoneNumber: phoneNumberModel.value,
                    optOutFromNewsletter: isNewsletterOptOutLogic ? newsletterTickedModel.value : !newsletterTickedModel.value,
                },
            };
            if (countryModel.value) {
                payload.userDetailsRequest.countryCode = countryModel.value;
            }
            const response = await signupApi.saveUserDetails(payload);
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(response);
            return {data: response, error: null};
        } catch (e) {
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function savePhoneNumber(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const payload = {
                phoneNumberRequest: {
                    phoneNumber: phoneNumberModel.value,
                },
            };
            const response = await signupApi.savePhoneNumber(payload);
            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(response);
            return {data: response, error: null};
        } catch (e) {
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function saveCompanyDetails(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const companySize = companySizeModel.value;
            const clientDepartment = clientDepartmentModel.value;
            const companyIndustry = companyIndustryModel.value;
            const payload = {
                companyDetailsRequest: {
                    companyName: companyNameModel.value || undefined,
                    companySize: instanceOfCompanySizeRequest(companySize) ? (companySize as CompanySizeRequest) : undefined,
                    companyWebsiteUrl: companyWebsiteUrlModel.value || undefined,
                    clientDepartment: instanceOfClientDepartmentRequest(clientDepartment) ? (clientDepartment as ClientDepartmentRequest) : undefined,
                    // no need to pass the sector, as it is implied by the industry (and we only store the industry)
                    companyIndustry: instanceOfCompanyIndustryRequest(companyIndustry) ? (companyIndustry as CompanyIndustryRequest) : undefined,
                    companyLegalDetails: companyLegalDetailsModel.value || undefined,
                },
            };
            const response = await signupApi.saveCompanyDetails(payload);

            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(response);
            return {data: response, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function saveUserQualification(): Promise<ResponseWrapper<SignupStateResponse>> {
        try {
            pendingRequest.value = true;
            const response = await signupApi.saveUserQualification();

            updateSignupInProgressCookieAndSetRedirectPathIfNeeded(response);
            return {data: response, error: null};
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
            return {data: null, error: e as FetchError};
        } finally {
            pendingRequest.value = false;
        }
    }

    async function answerQuestionUserQualification(questionAnswers: QuestionAnswersRequest): Promise<void> {
        try {
            pendingRequest.value = true;
            await signupApi.answerQuestionUserQualification({questionAnswersRequest: questionAnswers});
        } catch (e) {
            logger.error(e);
            pushVJoySnackbar({props: {message: t('signup.client.server.error'), level: 'error'}});
        } finally {
            pendingRequest.value = false;
        }
    }

    async function prefillFromLegalInformationSelection(
        prefill: Pick<LegalNameAutocompleteUpdatePayload, 'companySize' | 'businessSector' | 'industry' | 'websiteUrl'>,
    ): Promise<void> {
        if (prefill.companySize) companySizeModel.value = mapLegalCompanySizeToSignup(prefill.companySize);
        if (prefill.businessSector) {
            companySectorModel.value = prefill.businessSector;
            await nextTick(); // wait for the sector watcher to reset companyIndustryModel before setting it
        }
        if (prefill.industry) companyIndustryModel.value = prefill.industry;
        if (prefill.websiteUrl) companyWebsiteUrlModel.value = prefill.websiteUrl;
    }

    function resetRedirectPath() {
        redirectPath.value = '';
    }

    return {
        // state
        pendingRequest: readonlyState(pendingRequest),
        redirectedOnEmailValidation: readonlyState(redirectedOnEmailValidation),
        emailModel: readonlyState(emailModel),
        emailValidity: readonlyState(emailValidity),
        passwordModel: readonlyState(passwordModel),
        newsletterTickedModel: readonlyState(newsletterTickedModel),
        validationCodeModel: readonlyState(validationCodeModel),
        firstNameModel: readonlyState(firstNameModel),
        lastNameModel: readonlyState(lastNameModel),
        countryModel: readonlyState(countryModel),
        phoneNumberModel: readonlyState(phoneNumberModel),
        companyNameModel: companyNameModel,
        companyLegalDetailsModel: companyLegalDetailsModel,
        companyWebsiteUrlModel: readonlyState(companyWebsiteUrlModel),
        companySizeModel: readonlyState(companySizeModel),
        clientDepartmentModel: readonlyState(clientDepartmentModel),
        companySectorModel: readonlyState(companySectorModel),
        companyIndustryModel: readonlyState(companyIndustryModel),
        redirectPath: readonlyState(redirectPath),
        // actions
        getSignupState,
        cancelSignup,
        setRedirectedOnEmailValidation,
        getEmailAddressValidity,
        resetEmailValidity,
        saveAuthenticationDetails,
        acknowledgePublicEmailWarningPage,
        userAskToChangeEmail,
        requestNewValidationCode,
        resetEmailAndValidationCode,
        validateEmailAddress,
        chooseCompanyToJoin,
        saveUserDetails,
        saveCompanyDetails,
        savePhoneNumber,
        saveUserQualification,
        answerQuestionUserQualification,
        prefillFromLegalInformationSelection,
        resetRedirectPath,
    };
});
